/**
 * Edit-profile screen (route: EditProfile).
 *
 * The single "edit everything" surface for the current user's public profile:
 * avatar/banner images, display name, USERNAME, bio, location and website — the
 * fields that used to be split between this screen and app/settings/account.tsx
 * now live together here, Apple-aligned (translucent chrome + grouped glass
 * cards).
 *
 * MIGRATION (bare RN, P3-6): ported off Expo —
 *   expo-router          -> @react-navigation (useNavigation).
 *   @expo/vector-icons   -> components/ui/Icon shim.
 *   expo-image-picker    -> react-native-image-picker (launchImageLibrary;
 *                           result.assets[0].uri). RN-image-picker triggers the
 *                           OS permission prompt itself, so the explicit
 *                           requestMediaLibraryPermissionsAsync() call is dropped.
 * Uploads still go through services/api (uploadAvatar / uploadMedia).
 *
 * REDESIGN (P1-PROFILE, Apple):
 *   - The App.tsx registration for this route ships an OPAQUE native header. We
 *     can't touch App.tsx (shared), so we hide the native header via setOptions
 *     and render our OWN in-screen translucent GlassSurface bar (back chevron +
 *     centered title + Save GlassButton). ParticleBackground now shows through
 *     and content scrolls under the bar (skill §12 materials/depth).
 *   - Save is a real GlassButton (material + press response), not a header text
 *     link (skill §1 response).
 *   - Fields are grouped into hairline-divided glass cards (IDENTITY / ABOUT /
 *     LINKS), mirroring account.tsx's FieldRow for spatial consistency (skill §7,
 *     §15). Inputs use a translucent focus material, not a flat fill.
 *
 * API endpoints:
 *   GET   /auth/me           — load current profile values
 *   POST  /uploads/media     — multipart image upload (avatar/banner)
 *   PATCH /users/me          — persist profile fields + resolved image URLs
 *
 * Key behavior:
 *   - Image flow: pickImage stores a LOCAL uri (preview only); the actual upload
 *     happens on save. handleSave uploads any changed image first, and aborts
 *     the whole save if an upload fails (so we never persist a half-updated row).
 *   - `hasChanges` compares against the original snapshot (plus pending local
 *     images) to drive the Save button's enabled state.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  useWindowDimensions,
} from 'react-native';
import CachedImage from '../components/ui/CachedImage';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '../components/ui/Icon';
import LinearGradient from 'react-native-linear-gradient';
import { launchImageLibrary } from 'react-native-image-picker';
import { api, uploadMedia, uploadAvatar, getLastUploadError } from '../services/api';
import { hasAutoGeneratedIdentity } from '../lib/identity';
import { tabularNums } from '../lib/format';
import { GlassCard, GlassButton, GlassPill, GlassSurface } from '../components/glass';
import ParticleBackground from '../components/ui/ParticleBackground';
import LoadingState from '../components/ui/LoadingState';
import { colors, spacing, radius, fonts, typographyScale, shadowTokens, getLayout } from '../constants/theme';
import type { RootStackParamList } from '../navigation/types';

type Nav = NativeStackNavigationProp<RootStackParamList, 'EditProfile'>;

const BIO_MAX = 160;
const NAME_MAX = 50;
const LOCATION_MAX = 100;
const WEBSITE_MAX = 200;
// Loose URL check for inline validation — only flags obviously-bad input, never
// blocks save (backend is the source of truth). Empty = no error.
const URL_RE = /^(https?:\/\/)?[^\s.]+\.[^\s]{2,}$/i;
// Username rules mirror the backend (3–32 chars, letters/numbers/_/- only) and
// account.tsx exactly. Advisory: only blocks save when the field CHANGED and is
// invalid; the backend also enforces uniqueness.
const USERNAME_RE = /^[a-zA-Z0-9_-]{3,32}$/;
// In-screen translucent header height (native header is hidden — see setOptions).
const HEADER_HEIGHT = 44;

interface ProfileData {
  display_name: string;
  username: string;
  bio: string;
  location: string;
  website: string;
  avatar_url: string;
  banner_url: string;
}

const EMPTY_PROFILE: ProfileData = {
  display_name: '',
  username: '',
  bio: '',
  location: '',
  website: '',
  avatar_url: '',
  banner_url: '',
};

export default function EditProfileScreen() {
  const navigation = useNavigation<Nav>();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<ProfileData>(EMPTY_PROFILE);
  const [original, setOriginal] = useState<ProfileData>(EMPTY_PROFILE);
  // Local (un-uploaded) image uris chosen by the picker; null until the user
  // selects a new image. These preview immediately and upload on save.
  const [avatarLocal, setAvatarLocal] = useState<string | null>(null);
  const [bannerLocal, setBannerLocal] = useState<string | null>(null);
  // Triage P0 identity (IMG_5938/5943): true when the server display name is
  // just the auto-generated handle (`d967bbgzv2`). We then present an EMPTY
  // field + an invitation to pick a real name instead of echoing the garbage.
  const [autoGenName, setAutoGenName] = useState(false);
  // Which field currently has focus — drives the accent border/ring.
  const [focused, setFocused] = useState<keyof ProfileData | null>(null);
  // Whether an image is still resolving, so we can show a shimmer/spinner over the
  // avatar instead of a jarring empty circle ("loading-avatar look" fix).
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [bannerLoading, setBannerLoading] = useState(false);

  // Inline website validation — advisory only (never blocks save).
  const websiteError =
    data.website.trim() && !URL_RE.test(data.website.trim())
      ? 'That doesn’t look like a valid URL'
      : null;

  // Inline username validation — advisory. Only surfaces (and blocks save) once
  // the value has changed from the loaded original and is malformed.
  const usernameError = useMemo(() => {
    if (data.username === original.username) return null;
    if (!data.username.trim()) return null;
    return USERNAME_RE.test(data.username.trim()) ? null : 'Use 3–32 letters, numbers, _ or -';
  }, [data.username, original.username]);

  // Enable Save when any text field differs from the loaded original, or a new
  // local image is pending.
  const hasChanges =
    data.display_name !== original.display_name ||
    data.username !== original.username ||
    data.bio !== original.bio ||
    data.location !== original.location ||
    data.website !== original.website ||
    !!avatarLocal ||
    !!bannerLocal;

  const canSave = hasChanges && !saving && !usernameError;

  // --- Data load ---
  // Pull current profile and seed both the editable copy (`data`) and the
  // pristine snapshot (`original`) used for change detection.
  const fetchProfile = useCallback(async () => {
    setLoading(true);
    // /auth/me returns { data: { user: {...} } } with camelCase fields; api()
    // unwraps one level, so the user is at res.data.user.
    const res = await api<{ user: any }>('/auth/me');
    const u = res.success ? res.data?.user : null;
    if (u) {
      // Friendly identity: when the display name is machine-generated (equals
      // the auto handle), seed the field EMPTY so the placeholder invites a
      // real name. Save omits displayName while empty (never wipes the row).
      const isAutoGen = hasAutoGeneratedIdentity(u.displayName, u.username);
      setAutoGenName(isAutoGen);
      const d: ProfileData = {
        display_name: isAutoGen ? '' : (u.displayName || ''),
        username: u.username || '',
        bio: u.bio || '',
        location: u.location || '',
        website: u.website || '',
        avatar_url: u.avatar || '',
        banner_url: u.banner || '',
      };
      setData(d);
      setOriginal(d);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Hide the native (opaque) header registered in App.tsx so our in-screen
  // translucent GlassSurface bar can float over ParticleBackground with content
  // scrolling underneath (skill §12). App.tsx is shared, so we override here.
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  // --- Image picking & upload ---
  // Open the photo library (RN-image-picker prompts for permission on launch),
  // and store the chosen uri locally for preview. No upload here.
  async function pickImage(type: 'avatar' | 'banner') {
    try {
      const result = await launchImageLibrary({
        mediaType: 'photo',
        selectionLimit: 1,
        quality: 0.8,
        // Downscale before upload (#83): a raw 12MP phone photo is 3–5 MB and
        // was 413-ing ("too much") / timing out ("saves for a long time"). The
        // avatar is server-resized to 512 anyway; 1600 is plenty for the banner.
        maxWidth: 1600,
        maxHeight: 1600,
        includeBase64: false,
      });

      if (result.didCancel) return;
      if (result.errorCode) {
        Alert.alert('Error', result.errorMessage || 'Could not open the photo library. Please try again.');
        return;
      }

      const uri = result.assets?.[0]?.uri;
      if (uri) {
        if (type === 'avatar') {
          setAvatarLocal(uri);
        } else {
          setBannerLocal(uri);
        }
      }
    } catch {
      Alert.alert('Error', 'Could not open the photo library. Please try again.');
    }
  }

  // Upload a single local image and return its public URL (or null on failure).
  // Delegates to the shared upload service in services/api so multipart handling,
  // URL normalization, AND silent token refresh-on-401 all live in one place —
  // otherwise an expired access token here surfaces as "Failed to upload image".
  // The avatar goes to the dedicated /uploads/avatar endpoint (server resizes to
  // 512²); the banner stays on /uploads/media (no square-crop, larger allowance).
  async function uploadImage(uri: string, field: string): Promise<string | null> {
    return field === 'avatar' ? uploadAvatar(uri) : uploadMedia(uri);
  }

  // --- Save ---
  // Upload any changed images first, then PATCH the profile. If an image upload
  // fails we bail out before the PATCH so the saved record never references a
  // missing/half-uploaded asset. On success, notify and navigate back.
  const handleSave = useCallback(async () => {
    if (saving) return;

    // Validate a changed username locally before hitting the API (mirrors the
    // backend rules). The backend also enforces uniqueness with a clear error.
    const usernameChanged = data.username !== original.username;
    if (usernameChanged && !USERNAME_RE.test(data.username.trim())) {
      Alert.alert(
        'Invalid username',
        'Username must be 3–32 characters and use only letters, numbers, underscores, or hyphens.'
      );
      return;
    }

    setSaving(true);

    try {
      let avatarUrl = data.avatar_url;
      let bannerUrl = data.banner_url;
      // #83/#84: an image-upload failure must NOT abort the whole save — the text
      // edits (name, bio, links) should still persist. Collect any upload failures
      // and warn AFTER the profile PATCH, instead of returning early.
      const uploadFailures: string[] = [];

      // Upload images if changed
      if (avatarLocal) {
        const url = await uploadImage(avatarLocal, 'avatar');
        if (url) avatarUrl = url;
        else uploadFailures.push('photo');
      }

      if (bannerLocal) {
        const url = await uploadImage(bannerLocal, 'banner');
        if (url) bannerUrl = url;
        else uploadFailures.push('cover');
      }

      // Backend PATCH /users/me only accepts camelCase keys (displayName, avatar,
      // banner, ...); snake_case keys are silently dropped, so name/avatar/banner
      // edits would never persist. Send the names the backend actually whitelists.
      // Identity guard: an EMPTY display name is only ever the "auto-generated
      // name presented as blank" case — omit the key so we never PATCH an empty
      // displayName over the row. Typing a real name sends it as usual.
      // Username is whitelisted too (verified in account.tsx); send it only when
      // it changed, lowercased (the backend lowercases it anyway).
      const trimmedName = data.display_name.trim();
      const res = await api('/users/me', {
        method: 'PATCH',
        body: JSON.stringify({
          ...(trimmedName ? { displayName: trimmedName } : {}),
          ...(usernameChanged ? { username: data.username.trim().toLowerCase() } : {}),
          bio: data.bio,
          location: data.location,
          website: data.website,
          avatar: avatarUrl,
          banner: bannerUrl,
        }),
      });

      setSaving(false);

      if (res.success) {
        // A real display name was saved → the identity is no longer auto-gen.
        if (trimmedName) setAutoGenName(false);
        // Only clear the local previews whose upload actually succeeded, so a
        // failed image stays pending for a retry (its text still saved).
        if (!uploadFailures.includes('photo')) setAvatarLocal(null);
        if (!uploadFailures.includes('cover')) setBannerLocal(null);
        // Update the original snapshot to the saved values. The backend
        // lowercases the username — reflect what was actually saved.
        const saved: ProfileData = {
          ...data,
          username: usernameChanged ? data.username.trim().toLowerCase() : data.username,
          avatar_url: avatarUrl,
          banner_url: bannerUrl,
        };
        setData(saved);
        setOriginal(saved);
        if (uploadFailures.length) {
          // Text saved; only the image(s) failed. Keep the user on the screen to retry.
          Alert.alert(
            'Saved (except image)',
            `Your profile was updated, but the ${uploadFailures.join(' and ')} couldn't upload — ${getLastUploadError() || 'please try that image again.'}`,
          );
        } else {
          Alert.alert('Saved', 'Your profile has been updated.');
          navigation.goBack();
        }
      } else {
        Alert.alert('Error', res.error || 'Failed to update profile.');
      }
    } catch (err: any) {
      setSaving(false);
      Alert.alert('Error', err?.message || 'Something went wrong. Please try again.');
    }
  }, [saving, data, original.username, avatarLocal, bannerLocal, navigation]);

  if (loading) {
    return (
      <View style={styles.screen}>
        <ParticleBackground theme="light" />
        <LoadingState message="Loading…" onRetry={fetchProfile} />
      </View>
    );
  }

  // Prefer the pending local preview over the persisted URL while editing.
  const avatarDisplay = avatarLocal || data.avatar_url;
  const bannerDisplay = bannerLocal || data.banner_url;
  const nameOver = data.display_name.length > NAME_MAX;

  return (
    <View style={styles.screen}>
      <ParticleBackground theme="light" />

      {/* In-screen translucent header — floats over the particles; content
          scrolls under it (skill §12). Replaces the opaque native bar. */}
      <GlassSurface
        variant="bar"
        radius={0}
        sheen={false}
        surfaceColor="transparent"
        style={[styles.header, styles.headerTransparent, { paddingTop: insets.top, height: HEADER_HEIGHT + insets.top }]}
        contentStyle={styles.headerContent}
      >
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          accessibilityRole="button"
          accessibilityLabel="Back"
          style={styles.headerBack}
        >
          <Ionicons name="chevron-back" size={22} color={colors.ink} />
        </TouchableOpacity>
        <Text style={styles.headerTitle} numberOfLines={1}>Edit Profile</Text>
        <View style={styles.headerRight}>
          <GlassButton
            title="Save"
            onPress={handleSave}
            variant="primary"
            size="sm"
            disabled={!canSave}
            loading={saving}
          />
        </View>
      </GlassSurface>

      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={[
            styles.scrollContent,
            { paddingTop: HEADER_HEIGHT + insets.top },
            containerStyle,
          ]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Banner */}
          <TouchableOpacity
            style={styles.bannerWrap}
            onPress={() => pickImage('banner')}
            activeOpacity={0.85}
            accessibilityRole="button"
            accessibilityLabel="Change banner image"
          >
            {bannerDisplay ? (
              <CachedImage
                source={{ uri: bannerDisplay }}
                style={styles.banner}
                onLoadStart={() => setBannerLoading(true)}
                onLoadEnd={() => setBannerLoading(false)}
              />
            ) : (
              // Triage P1 (IMG_5939): default banner was a pure-black slab —
              // use the brand lavender gradient instead.
              <LinearGradient
                colors={colors.gradient}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.banner}
              />
            )}
            {/* Bottom scrim so the "Edit cover" pill stays legible on any image */}
            <LinearGradient
              colors={['rgba(26,26,46,0)', 'rgba(26,26,46,0.45)']}
              style={styles.bannerScrim}
              pointerEvents="none"
            />
            <View style={styles.bannerChipWrap} pointerEvents="none">
              <GlassPill label="Edit cover" icon="camera-outline" />
            </View>
            {bannerLoading && (
              <View style={styles.mediaSpinner} pointerEvents="none">
                <ActivityIndicator size="small" color={colors.white} />
              </View>
            )}
          </TouchableOpacity>

          {/* Avatar */}
          <View style={styles.avatarSection}>
            <TouchableOpacity
              style={styles.avatarWrap}
              onPress={() => pickImage('avatar')}
              activeOpacity={0.85}
              accessibilityRole="button"
              accessibilityLabel="Change profile photo"
            >
              <View style={styles.avatarRing}>
                {avatarDisplay ? (
                  <CachedImage
                    source={{ uri: avatarDisplay }}
                    style={styles.avatar}
                    onLoadStart={() => setAvatarLoading(true)}
                    onLoadEnd={() => setAvatarLoading(false)}
                  />
                ) : (
                  // No jarring blank circle — a branded gradient placeholder.
                  <LinearGradient
                    colors={colors.gradient}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={[styles.avatar, styles.avatarFallback]}
                  >
                    <Ionicons name="person" size={34} color={colors.white} />
                  </LinearGradient>
                )}
                {avatarLoading && avatarDisplay && (
                  <View style={[styles.avatar, styles.avatarSpinner]} pointerEvents="none">
                    <ActivityIndicator size="small" color={colors.white} />
                  </View>
                )}
              </View>
              <View style={styles.avatarBadge}>
                <Ionicons name="camera" size={14} color={colors.white} />
              </View>
            </TouchableOpacity>
            <Text style={styles.avatarHint}>Tap to change your photo</Text>
          </View>

          {/* Fields */}
          <View style={styles.fieldsWrap}>
            {/* Group A — IDENTITY: display name + username */}
            <View>
              <Text style={styles.sectionTitle}>Identity</Text>
              <GlassCard padding={0}>
                <View style={[styles.fieldRow, styles.fieldRowBorder]}>
                  <View style={styles.fieldHeader}>
                    <Ionicons name="person-outline" size={15} color={colors.muted} />
                    <Text style={styles.fieldLabel}>Display Name</Text>
                  </View>
                  <TextInput
                    style={[
                      styles.fieldInput,
                      focused === 'display_name' && styles.fieldInputFocused,
                      nameOver && styles.fieldInputError,
                    ]}
                    value={data.display_name}
                    onChangeText={(v) => setData({ ...data, display_name: v })}
                    onFocus={() => setFocused('display_name')}
                    onBlur={() => setFocused(null)}
                    placeholder={autoGenName ? 'Pick a name people know you by' : 'Your display name'}
                    placeholderTextColor={colors.faint}
                    autoCapitalize="words"
                    maxLength={NAME_MAX}
                  />
                  {autoGenName && !data.display_name.trim() ? (
                    <Text style={styles.fieldHint}>
                      You're currently shown by an auto-generated ID — add a display
                      name so people recognize you.
                    </Text>
                  ) : null}
                </View>

                <View style={styles.fieldRow}>
                  <View style={styles.fieldHeader}>
                    <Ionicons name="at-outline" size={15} color={colors.muted} />
                    <Text style={styles.fieldLabel}>Username</Text>
                  </View>
                  <View
                    style={[
                      styles.usernameField,
                      focused === 'username' && styles.fieldInputFocused,
                      !!usernameError && styles.fieldInputError,
                    ]}
                  >
                    <Text style={styles.usernamePrefix}>@</Text>
                    <TextInput
                      style={styles.usernameInput}
                      value={data.username}
                      onChangeText={(v) => setData({ ...data, username: v.replace(/[^a-zA-Z0-9_-]/g, '') })}
                      onFocus={() => setFocused('username')}
                      onBlur={() => setFocused(null)}
                      placeholder="username"
                      placeholderTextColor={colors.faint}
                      autoCapitalize="none"
                      autoCorrect={false}
                      maxLength={32}
                    />
                  </View>
                  {usernameError ? (
                    <Text style={styles.fieldError}>{usernameError}</Text>
                  ) : (
                    <Text style={styles.fieldCaption}>3–32 characters · letters, numbers, _ and -</Text>
                  )}
                </View>
              </GlassCard>
            </View>

            {/* Group B — ABOUT: bio */}
            <View>
              <Text style={styles.sectionTitle}>About</Text>
              <GlassCard padding={0}>
                <View style={styles.fieldRow}>
                  <View style={styles.fieldLabelRow}>
                    <View style={styles.fieldHeader}>
                      <Ionicons name="document-text-outline" size={15} color={colors.muted} />
                      <Text style={styles.fieldLabel}>Bio</Text>
                    </View>
                    <Text
                      style={[
                        styles.charCount,
                        data.bio.length >= BIO_MAX && styles.charCountOver,
                      ]}
                    >
                      {data.bio.length}/{BIO_MAX}
                    </Text>
                  </View>
                  <TextInput
                    style={[
                      styles.fieldInput,
                      styles.bioInput,
                      focused === 'bio' && styles.fieldInputFocused,
                    ]}
                    value={data.bio}
                    onChangeText={(v) => {
                      if (v.length <= BIO_MAX) setData({ ...data, bio: v });
                    }}
                    onFocus={() => setFocused('bio')}
                    onBlur={() => setFocused(null)}
                    placeholder="Tell the world about yourself"
                    placeholderTextColor={colors.faint}
                    multiline
                    maxLength={BIO_MAX}
                    textAlignVertical="top"
                  />
                </View>
              </GlassCard>
            </View>

            {/* Group C — LINKS: location + website (+ v2 add-link teaser) */}
            <View>
              <Text style={styles.sectionTitle}>Links</Text>
              <GlassCard padding={0}>
                <View style={[styles.fieldRow, styles.fieldRowBorder]}>
                  <View style={styles.fieldHeader}>
                    <Ionicons name="location-outline" size={15} color={colors.muted} />
                    <Text style={styles.fieldLabel}>Location</Text>
                  </View>
                  <TextInput
                    style={[
                      styles.fieldInput,
                      focused === 'location' && styles.fieldInputFocused,
                    ]}
                    value={data.location}
                    onChangeText={(v) => setData({ ...data, location: v })}
                    onFocus={() => setFocused('location')}
                    onBlur={() => setFocused(null)}
                    placeholder="Where are you based?"
                    placeholderTextColor={colors.faint}
                    maxLength={LOCATION_MAX}
                  />
                </View>

                <View style={styles.fieldRow}>
                  <View style={styles.fieldHeader}>
                    <Ionicons name="link-outline" size={15} color={colors.muted} />
                    <Text style={styles.fieldLabel}>Website</Text>
                  </View>
                  <TextInput
                    style={[
                      styles.fieldInput,
                      focused === 'website' && styles.fieldInputFocused,
                      !!websiteError && styles.fieldInputError,
                    ]}
                    value={data.website}
                    onChangeText={(v) => setData({ ...data, website: v })}
                    onFocus={() => setFocused('website')}
                    onBlur={() => setFocused(null)}
                    placeholder="https://yoursite.com"
                    placeholderTextColor={colors.faint}
                    keyboardType="url"
                    autoCapitalize="none"
                    autoCorrect={false}
                    maxLength={WEBSITE_MAX}
                  />
                  {websiteError ? <Text style={styles.fieldError}>{websiteError}</Text> : null}
                </View>
              </GlassCard>
            </View>
          </View>

          <View style={{ height: spacing.xxxl * 2 }} />
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const BANNER_HEIGHT = 160;
const AVATAR_SIZE = 88;

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  loadingWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
  headerTransparent: {
    borderWidth: 0,
    borderBottomWidth: 0,
    shadowOpacity: 0,
    elevation: 0,
    backgroundColor: 'transparent',
  },
  headerContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: HEADER_HEIGHT,
    paddingHorizontal: spacing.sm,
  },
  headerBack: {
    position: 'absolute',
    left: spacing.sm,
    height: HEADER_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: spacing.xs,
  },
  headerTitle: {
    ...typographyScale.h3,
    color: colors.ink,
    ...fonts.semibold,
  },
  headerRight: {
    position: 'absolute',
    right: spacing.md,
  },
  scrollContent: {
    paddingBottom: spacing.xxxl,
  },
  bannerWrap: {
    position: 'relative',
    height: BANNER_HEIGHT,
  },
  banner: {
    width: '100%',
    height: BANNER_HEIGHT,
  },
  bannerScrim: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: BANNER_HEIGHT / 2,
  },
  bannerChipWrap: {
    position: 'absolute',
    right: spacing.lg,
    bottom: spacing.md,
  },
  mediaSpinner: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(26,26,46,0.25)',
  },
  avatarSection: {
    paddingHorizontal: spacing.lg,
    marginTop: -(AVATAR_SIZE / 2),
    marginBottom: spacing.lg,
  },
  avatarWrap: {
    position: 'relative',
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
  },
  avatarRing: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: AVATAR_SIZE / 2,
    borderWidth: 3,
    borderColor: colors.white,
    overflow: 'hidden',
    ...shadowTokens.card,
  },
  avatar: {
    width: '100%',
    height: '100%',
    borderRadius: AVATAR_SIZE / 2,
  },
  avatarFallback: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarSpinner: {
    position: 'absolute',
    top: 0,
    left: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(26,26,46,0.3)',
  },
  avatarBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: colors.white,
  },
  avatarHint: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: spacing.sm,
    ...fonts.medium,
  },
  fieldsWrap: {
    paddingHorizontal: spacing.lg,
    gap: spacing.md,
  },
  sectionTitle: {
    ...typographyScale.caption,
    color: colors.muted,
    marginBottom: spacing.sm,
    marginLeft: spacing.xs,
    marginTop: spacing.lg,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    ...fonts.semibold,
  },
  fieldRow: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  fieldRowBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.cardBorder,
  },
  fieldHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs,
  },
  fieldLabelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  fieldLabel: {
    ...typographyScale.caption,
    color: colors.muted,
    ...fonts.semibold,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  charCount: {
    ...typographyScale.caption,
    color: colors.muted,
    marginBottom: spacing.xs,
    ...fonts.regular,
    ...tabularNums,
  },
  charCountOver: {
    color: colors.error,
    ...fonts.semibold,
  },
  fieldHint: {
    ...typographyScale.caption,
    lineHeight: 17,
    color: colors.primary,
    marginTop: spacing.sm,
    ...fonts.medium,
  },
  fieldCaption: {
    ...typographyScale.caption,
    fontSize: 11,
    color: colors.muted,
    marginTop: spacing.xs,
    ...fonts.regular,
  },
  fieldError: {
    ...typographyScale.caption,
    fontSize: 11,
    color: colors.error,
    marginTop: spacing.xs,
    ...fonts.medium,
  },
  fieldInput: {
    ...typographyScale.body,
    fontSize: 16,
    color: colors.ink,
    backgroundColor: 'rgba(255,255,255,0.55)',
    borderRadius: radius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    ...fonts.regular,
  },
  fieldInputFocused: {
    borderColor: colors.primary,
    backgroundColor: colors.white,
  },
  fieldInputError: {
    borderColor: colors.error,
  },
  bioInput: {
    height: 88,
    paddingTop: spacing.sm,
  },
  usernameField: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.55)',
    borderRadius: radius.md,
    paddingHorizontal: spacing.md,
    borderWidth: 1,
    borderColor: colors.cardBorder,
  },
  usernamePrefix: {
    fontSize: 16,
    color: colors.muted,
    ...fonts.regular,
  },
  usernameInput: {
    flex: 1,
    fontSize: 16,
    color: colors.ink,
    paddingVertical: spacing.sm,
    paddingLeft: 2,
    ...fonts.regular,
  },
});
